import { Component, HostListener, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-maintenance',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="maintenance-view">
      <!-- Cinematic Background -->
      <div class="video-container">
        <video autoplay muted loop playsinline class="bg-video">
          <source
            src="https://assets.mixkit.co/videos/preview/mixkit-luxury-yacht-on-the-sea-from-above-1560-large.mp4"
            type="video/mp4"
          />
        </video>
        <div class="gradient-overlay"></div>
      </div>

      <!-- Immersive Content Area -->
      <main class="immersive-content">
        <div class="content-left">
          <div class="brand-header">
            <img
              src="/lcys-logo-light-blue.svg"
              alt="LCYS Logo"
              class="brand-logo"
              onerror="this.src='/lcys-logo-light-blue.png'"
            />
          </div>

          <section class="hero-section">
            <div class="hero-title-group">
              <h1 class="hero-title">Refining the <br /><span class="accent">Experience</span></h1>
              <p class="hero-subtitle">
                We're currently navigating through updates to bring you an even more refined
                maritime destination. Our luxury fleet will be back at your service very soon.
              </p>
            </div>

            <div class="status-indicator">
              <div class="indicator-ring">
                <div class="indicator-dot"></div>
              </div>
              <span class="indicator-text">Harbor Operations Underway</span>
            </div>

            <div class="cta-section">
              <button class="action-btn" (click)="refresh()" [disabled]="isRefreshing">
                <span class="btn-ripple"></span>
                <span class="btn-label">{{
                  isRefreshing ? 'Checking Harbor...' : 'Check Connection'
                }}</span>
                <svg
                  *ngIf="!isRefreshing"
                  class="btn-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke-width="1.5" />
                  <path
                    d="M9 12l2 2 4-4"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div *ngIf="isRefreshing" class="loader-spinner"></div>
              </button>

              <a routerLink="/admin/login" class="admin-link">Login as Admin</a>
            </div>
          </section>

          <footer class="immersive-footer">
            <div class="footer-line"></div>
            <div class="footer-content">
              <p class="copyright">
                &copy; 2026 &bull; LEAGUE CITY YACHT SALES &bull; ALL RIGHTS RESERVED
              </p>
              <p class="establish-date">ESTABLISHED 1956</p>
            </div>
          </footer>
        </div>
      </main>

      <!-- Right Panel: Interactive Maritime Mesh -->
      <div class="content-right">
        <div
          class="interactive-mesh"
          [style.transform]="'translate(' + mouseX + 'px,' + mouseY + 'px)'"
        >
          <div class="mesh-point" *ngFor="let p of [1, 2, 3, 4, 5, 6]"></div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;600&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');

      :host {
        --primary-gold: #d4af37;
        --bg-dark: #020617;
        --text-main: #f8fafc;
        --text-muted: rgba(248, 250, 252, 0.6);
      }

      .maintenance-view {
        position: fixed;
        inset: 0;
        width: 100vw;
        height: 100vh;
        background: var(--bg-dark);
        color: var(--text-main);
        font-family: 'Outfit', sans-serif;
        overflow: hidden;
        z-index: 999999;
      }

      /* Background Layer */
      .video-container {
        position: absolute;
        inset: 0;
        z-index: 1;

        .bg-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.45;
          transform: scale(1.05);
          animation: subtleZoom 20s infinite alternate linear;
        }

        .gradient-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom,
            rgba(2, 6, 23, 0.8) 0%,
            rgba(2, 6, 23, 0.4) 40%,
            rgba(2, 6, 23, 0.9) 100%
          );
        }
      }

      /* Content Layer */
      .immersive-content {
        position: relative;
        z-index: 10;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 4rem;
        padding: 4rem 6rem;
        max-width: 1600px;
        margin: 0 auto;

        @media (max-width: 1024px) {
          grid-template-columns: 1fr;
          padding: 3rem;
        }

        @media (max-width: 768px) {
          padding: 2rem;
          display: block;
        }
      }

      .content-left {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;

        @media (max-width: 768px) {
          text-align: center;
          align-items: center;
        }
      }

      .content-right {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 1024px) {
          display: none;
        }
      }

      .brand-header {
        margin-bottom: auto;
        animation: fadeInDown 1s ease-out;

        .brand-logo {
          height: clamp(40px, 6vw, 70px);
          width: auto;
          object-fit: contain;
          filter: drop-shadow(0 0 20px rgba(212, 175, 55, 0.2));

          @media (max-width: 768px) {
            margin: 1rem auto 2rem;
            display: block;
          }
        }

        @media (max-width: 768px) {
          position: relative;
          margin-bottom: 2rem;
        }
      }

      .hero-section {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        animation: fadeInLeft 1.2s cubic-bezier(0.2, 0.8, 0.2, 1);

        @media (max-width: 768px) {
          align-items: center;
        }
      }

      .hero-title {
        font-family: 'Playfair Display', serif;
        font-size: clamp(2.5rem, 8vw, 5.5rem);
        font-weight: 400;
        line-height: 1.1;
        margin-bottom: 2rem;
        letter-spacing: -0.02em;

        @media (max-width: 768px) {
          margin-top: 1rem;
        }

        .accent {
          color: var(--primary-gold);
          font-style: italic;
          background: linear-gradient(90deg, #d4af37, #f2d06b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }

      .hero-subtitle {
        font-size: 1.25rem;
        line-height: 1.8;
        color: var(--text-muted);
        margin-bottom: 3.5rem;
        max-width: 600px;
        font-weight: 300;
      }

      .status-indicator {
        display: flex;
        align-items: center;
        gap: 1.25rem;
        margin-bottom: 3rem;

        .indicator-ring {
          width: 24px;
          height: 24px;
          border: 1px solid rgba(212, 175, 55, 0.3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;

          .indicator-dot {
            width: 8px;
            height: 8px;
            background: var(--primary-gold);
            border-radius: 50%;
            box-shadow: 0 0 15px var(--primary-gold);
            animation: statusPulse 2s infinite;
          }
        }

        .indicator-text {
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: var(--primary-gold);
          font-weight: 600;
        }
      }

      .action-btn {
        position: relative;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        padding: 1.25rem 2.5rem;
        border-radius: 100px;
        color: white;
        font-size: 1rem;
        font-weight: 500;
        letter-spacing: 0.05em;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 1rem;
        transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;

        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.1);
          border-color: var(--primary-gold);
          transform: translateX(10px) scale(1.05);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);

          .btn-icon {
            transform: translateX(5px) rotate(360deg);
            color: var(--primary-gold);
          }
        }

        &:disabled {
          opacity: 0.8;
          cursor: wait;
        }

        .btn-icon {
          width: 20px;
          height: 20px;
          transition: all 0.6s ease;
        }

        .loader-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: var(--primary-gold);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
      }

      .admin-link {
        margin-top: 1.5rem;
        font-size: 0.85rem;
        color: var(--text-muted);
        text-decoration: none;
        transition: all 0.3s ease;
        letter-spacing: 0.05em;
        font-weight: 300;
        cursor: pointer;
        display: inline-block;

        &:hover {
          color: var(--primary-gold);
          text-decoration: underline;
          transform: translateY(-2px);
        }
      }

      .immersive-footer {
        padding-top: 2rem;
        animation: fadeInUp 1s ease-out;

        .footer-line {
          height: 1px;
          background: linear-gradient(90deg, var(--primary-gold), transparent);
          margin-bottom: 2rem;
          width: 150px;

          @media (max-width: 768px) {
            margin: 0 auto 1.5rem;
          }
        }

        .footer-content {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          @media (max-width: 768px) {
            text-align: center;
          }
        }

        .copyright {
          font-size: 0.8rem;
          letter-spacing: 0.3em;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .establish-date {
          font-size: 0.75rem;
          letter-spacing: 0.5em;
          color: var(--primary-gold);
          font-weight: 600;
          opacity: 0.6;
          margin-top: 0.5rem;
        }
      }

      /* Moving content-right outside immersive-content for better PC alignment */
      .content-right {
        position: absolute;
        top: 0;
        right: 0;
        width: 50%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 5;
        pointer-events: none;

        @media (max-width: 1024px) {
          display: none;
        }
      }

      /* Interactive Mesh Effect */
      .interactive-mesh {
        position: relative;
        width: 100%;
        height: 100%;
        transition: transform 0.2s ease-out;

        .mesh-point {
          position: absolute;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(40px);
          animation: meshFloat 15s infinite ease-in-out alternate;

          &:nth-child(1) {
            top: 10%;
            right: 20%;
            animation-delay: 0s;
            width: 400px;
            height: 400px;
          }
          &:nth-child(2) {
            top: 40%;
            right: 5%;
            animation-delay: -3s;
            background: radial-gradient(circle, rgba(14, 165, 233, 0.2) 0%, transparent 70%);
            width: 350px;
            height: 350px;
          }
          &:nth-child(3) {
            bottom: 20%;
            right: 25%;
            animation-delay: -6s;
            width: 450px;
            height: 450px;
          }
          &:nth-child(4) {
            top: 60%;
            right: 30%;
            animation-delay: -9s;
            background: radial-gradient(circle, rgba(212, 175, 55, 0.25) 0%, transparent 70%);
          }
          &:nth-child(5) {
            top: 20%;
            right: 40%;
            animation-delay: -12s;
            width: 250px;
            height: 250px;
          }
          &:nth-child(6) {
            bottom: 30%;
            right: 10%;
            animation-delay: -15s;
            background: radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 70%);
          }
        }
      }

      @keyframes meshFloat {
        0% {
          transform: translate(0, 0) scale(1);
          opacity: 0.4;
        }
        100% {
          transform: translate(50px, -40px) scale(1.3);
          opacity: 0.8;
        }
      }

      /* Animations */
      @keyframes subtleZoom {
        from {
          transform: scale(1);
        }
        to {
          transform: scale(1.15);
        }
      }

      @keyframes statusPulse {
        0% {
          transform: scale(1);
          opacity: 0.8;
        }
        50% {
          transform: scale(1.4);
          opacity: 1;
          box-shadow: 0 0 25px var(--primary-gold);
        }
        100% {
          transform: scale(1);
          opacity: 0.8;
        }
      }

      @keyframes floatOrb {
        from {
          transform: translate(0, 0) scale(1);
        }
        to {
          transform: translate(-30px, 40px) scale(1.1);
        }
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      @keyframes fadeInDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fadeInLeft {
        from {
          opacity: 0;
          transform: translateX(-40px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
  ],
})
export class MaintenanceComponent {
  isRefreshing = false;
  mouseX = 0;
  mouseY = 0;
  private platformId = inject(PLATFORM_ID);

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent) {
    if (!isPlatformBrowser(this.platformId)) return;
    // Subtle parallax effect
    this.mouseX = (e.clientX - (typeof window !== 'undefined' ? window.innerWidth : 0) / 2) / 25;
    this.mouseY = (e.clientY - (typeof window !== 'undefined' ? window.innerHeight : 0) / 2) / 25;
  }

  refresh() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.isRefreshing = true;
    setTimeout(() => {
      if (isPlatformBrowser(this.platformId)) {
        window.location.reload();
      }
    }, 1500);
  }
}
